import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Autocomplete,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import { getAssociates, getAssociateLedgerReport } from '../../services/associate';
import { hasReadPermission } from '../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import { Avatar } from '@mui/material';
import { useHistory } from 'react-router-dom';

const AssociateLedger = () => {
  const history = useHistory();
  // const canRead = hasReadPermission('reports.associate.associate-ledger') || hasReadPermission('associate.associate-ledger') || hasReadPermission('reports.associate-ledger') || hasReadPermission('associate-ledger');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    selectedAssociate: '',
    fromDate: '',
    toDate: '',
    searchTerm: '',
  });
  const [selectedAssociate, setSelectedAssociate] = useState(null);

  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    itemsPerPage: 10,
    currentPage: 1
  });

  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [associatesError, setAssociatesError] = useState(null);
  const [associateSearchInput, setAssociateSearchInput] = useState("");

  // Helper to format currency with 3 decimal places like the image (e.g. 70,000.000)
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    return Number(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Helper to format Date as DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setFilters({
      selectedAssociate: '',
      fromDate: '',
      toDate: '',
      searchTerm: '',
    });
    setSelectedAssociate(null);
    setAssociateSearchInput('');
    setPage(0);
  };

  const buildExportRows = () => {
    const rows = filteredData.map(row => ({
      'Date': row.date,
      'Type': row.type,
      'Head': row.particular.head || '',
      'Mode': row.particular.main || '',
      'Booking #': row.particular.sub5 || '',
      'Bank / Cheque': row.particular.sub4 || '',
      'Dr Amount': formatAmount(row.drAmount),
      'Cr Amount': formatAmount(row.crAmount),
      'Balance': formatAmount(row.balance),
      'Description': row.description || '',
    }));

    rows.push({
      'Date': '',
      'Type': '',
      'Head': '',
      'Mode': 'GRAND TOTAL',
      'Booking #': '',
      'Bank / Cheque': '',
      'Dr Amount': formatAmount(totalDebit),
      'Cr Amount': formatAmount(totalCredit),
      'Balance': formatAmount(closingBalance),
      'Description': '',
    });

    return rows;
  };

  const getExportFileName = () => `associate_ledger_${selectedAssociate?.name || 'report'}_${new Date().toISOString().slice(0, 10)}`;

  const handleExportPdf = () => {
    if (filteredData.length === 0) return;
    exportAsPDF({
      rows: buildExportRows(),
      title: `Associate Ledger — ${selectedAssociate?.name || ''}`,
      fileNameBase: getExportFileName(),
      orientation: 'l',
    });
  };

  const handleExportCsv = () => {
    if (filteredData.length === 0) return;
    exportAsCSV({
      rows: buildExportRows(),
      fileNameBase: getExportFileName(),
    });
  };

  const handleExportExcel = () => {
    if (filteredData.length === 0) return;
    exportAsExcel({
      rows: buildExportRows(),
      sheetName: 'Ledger',
      fileNameBase: getExportFileName(),
    });
  };


  const fetchAssociateLedger = async (associateId, currentPage = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: limit,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };
      const result = await getAssociateLedgerReport(associateId, params);
      if (result.success) {
        setApiData(result.data || []);
        if (result.pagination) {
          setPagination(result.pagination);
        } else {
          setPagination(prev => ({
            ...prev,
            totalItems: result.totalCount || result.data?.length || 0
          }));
        }
      } else {
        throw new Error(result.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching associate ledger:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociates = async (searchTerm = "") => {
    setAssociatesLoading(true);
    setAssociatesError(null);
    try {
      const params = {
        page: 1,
        limit: searchTerm && searchTerm.length > 0 ? 50 : 20
      };
      if (searchTerm && searchTerm.length > 0) {
        params.search = searchTerm;
      }
      const response = await getAssociates(params);
      if (response?.status && Array.isArray(response?.data)) {
        const docs = response.data;
        const normalized = docs
          .map((item) => {
            const codeCandidate = item?.associateId ?? item?._id ?? item?.id ?? item?.code ?? item?.email ?? "";
            const primaryName = [item?.name, item?.lastName].filter(Boolean).join(" ").trim() || item?.email || "";
            const code = String(codeCandidate || primaryName || "").trim();
            if (!code) return null;
            return {
              id: item?._id || item?.id || null,
              code: code,
              name: primaryName || code,
              mobile: (item?.phone ?? item?.whatsapp ?? item?.mobile ?? "").toString().trim(),
              designation: item?.designation || "",
              commission: item?.commission,
            };
          })
          .filter(Boolean);
        setAssociates(normalized);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setAssociatesError(err.message);
      setAssociates([]);
    } finally {
      setAssociatesLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAssociate && selectedAssociate.id) {
      fetchAssociateLedger(selectedAssociate.id, page + 1, rowsPerPage);
    } else {
      setApiData([]);
      setError(null);
      setPagination({
        totalItems: 0,
        totalPages: 0,
        itemsPerPage: rowsPerPage,
        currentPage: 1
      });
    }
  }, [selectedAssociate, page, rowsPerPage, filters.fromDate, filters.toDate]);

  useEffect(() => {
    fetchAssociates();
  }, []);

  useEffect(() => {
    if (associateSearchInput.length > 0) {
      const delayDebounceFn = setTimeout(() => {
        fetchAssociates(associateSearchInput);
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    } else {
      fetchAssociates("");
    }
  }, [associateSearchInput]);

  const transformApiData = (apiTransactions) => {
    if (!apiTransactions || apiTransactions.length === 0) return [];

    return apiTransactions.map((t, index) => {
      // Determine columns based on transactionType
      // In the image: "Commission Generate" is Credit, "Payment" is Debit.
      // In API: 'add' usually increases balance (Credit).

      const isCredit = t.transactionType === 'add';
      const drAmount = isCredit ? 0 : (t.amount || 0);
      const crAmount = isCredit ? (t.amount || 0) : 0;

      // Construct Type
      const type = isCredit ? "Payment Receive" : "Payment Paid";

      // Construct Particular Object (for multi-line rendering)
      const particularData = {
        main: `Mode: ${t.paymentMode || 'N/A'}`,
        sub1: t.tdsAmount > 0 ? `Tds Deduction : ${formatAmount(t.tdsAmount)}` : null,
        sub2: t.charge > 0 ? `Charges : ${formatAmount(t.charge)}` : null,
        sub3: t.csrAmount > 0 ? `CSR Amount : ${formatAmount(t.csrAmount)}` : null,
        sub8: t.marketingAmount > 0 ? `Marketing Amount : ${formatAmount(t.marketingAmount)}` : null,
        sub7: t.otherAmount > 0 ? `Other Amount : ${formatAmount(t.otherAmount)}` : null,
        sub9: (t.transactionType === 'deduct' && t.netAmount !== undefined && t.netAmount !== null) ? `Net Amount : ${formatAmount(t.netAmount)}` : null,
        // sub3: t.voucherId ? `Voucher # : ${t.voucherId}` : null,
        sub4: t.bankName ? `${t.bankName} ${t.chequeNumber ? `(${t.chequeNumber})` : ''}` : null,
        // ...(isCredit && {
        sub5: t.bookingIdNo ? `Booking # : ${t.bookingIdNo}` : null,
        sub6: t.inventoryName ? `Inventory : ${t.inventoryName}` : null,
        head: t.paymentHead || t.head || 'Commission Paid'
      };

      return {
        id: t._id || index,
        bookingId: t.bookingId || null,
        date: formatDate(t.createdAt),
        type: type,
        particular: particularData,
        drAmount: drAmount,
        crAmount: crAmount,
        balance: t.newBalance || 0,
        head: t.paymentHead || t.head || 'Commission Paid',
        paymentHead: t.paymentHead || 'Commission Paid',
        description: t.description || ""
      };
    });
  };

  const transformedData = useMemo(() => transformApiData(apiData), [apiData]);

  let filteredData = selectedAssociate ? transformedData : [];

  // Note: Date filtering is now handled server-side for consistency with pagination.
  // The following calculation provides totals for the CURRENT PAGE records.

  const totalDebit = filteredData.reduce((sum, item) => sum + item.drAmount, 0);
  const totalCredit = filteredData.reduce((sum, item) => sum + item.crAmount, 0);
  const closingBalance = filteredData.length > 0 ? filteredData[filteredData.length - 1].balance : 0;

  // if (!canRead) {
  //   return (
  //     <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
  //       <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
  //         <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
  //           <WarningIcon sx={{ fontSize: 50 }} />
  //         </Avatar>
  //         <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
  //           Access Denied
  //         </Typography>
  //         <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
  //           You do not have permission to view Associate Ledger Report. Please contact your administrator for access.
  //         </Typography>
  //         <Button
  //           variant="contained"
  //           onClick={() => history.push('/app/dashboard')}
  //           sx={{
  //             backgroundColor: '#536DFE',
  //             '&:hover': { backgroundColor: '#3d5afe' },
  //             px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
  //           }}
  //         >
  //           Back to Dashboard
  //         </Button>
  //       </Paper>
  //     </Box>
  //   );
  // }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Associate Ledger Report | {selectedAssociate ? selectedAssociate.name : 'Select Associate'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Applied Filters : | Associate Name: {selectedAssociate ? `${selectedAssociate.name} - ${selectedAssociate.code}` : 'None'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
            />
            <RefreshButton
              onClick={() => selectedAssociate && fetchAssociateLedger(selectedAssociate.id)}
              loading={loading}
              disabled={!selectedAssociate}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Search Filters */}
      <Card sx={{ mb: 3, borderRadius: 1, boxShadow: 1 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={associates}
                getOptionLabel={(option) => option ? `${option.name} (${option.code})` : ""}
                inputValue={associateSearchInput}
                onInputChange={(_, newInputValue) => setAssociateSearchInput(newInputValue)}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  setPage(0);
                }}
                loading={associatesLoading}
                renderInput={(params) => (
                  <TextField {...params} label="Search Associate" size="small" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth size="small" type="date" label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth size="small" type="date" label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Table Matching the Image */}
      <Card sx={{ borderRadius: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              {/* Image Header Style: Dark Blue/Grey Background */}
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderRight: '1px solid #546e7a' }}>Date</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderRight: '1px solid #546e7a' }}>Type</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderRight: '1px solid #546e7a', minWidth: 250 }}>Particular</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderRight: '1px solid #546e7a', textAlign: 'right' }}>Dr Amount</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderRight: '1px solid #546e7a', textAlign: 'right' }}>Cr Amount</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderRight: '1px solid #546e7a', textAlign: 'right' }}>Balance</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Opening Balance Row - Optional but common in ledgers */}
              <TableRow sx={{ '& td': { py: 1, borderBottom: '1px solid #e0e0e0' } }}>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Opening Balance</TableCell>
                <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>0.00</TableCell>
                <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>0.00</TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace' }}>0.00 CR</TableCell>
                <TableCell></TableCell>
              </TableRow>

              {loading ? (
                <TableRow><TableCell colSpan={6} align="center">Loading...</TableCell></TableRow>
              ) : filteredData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    verticalAlign: 'top',
                    '& td': {
                      py: 1.5,
                      borderRight: '1px solid #f0f0f0',
                      borderBottom: '1px solid #e0e0e0',
                      fontSize: '0.875rem'
                    },
                    '&:hover': { backgroundColor: '#fafafa' }
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>

                  <TableCell>{row.type}</TableCell>

                  {/* Particular Column - Multi-line rendering */}
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.particular.main}
                      </Typography>
                      {row.particular.head && (
                        <Typography variant="caption" display="block" sx={{ color: 'primary.main', fontWeight: 700, textTransform: 'uppercase' }}>
                          {row.particular.head}
                        </Typography>
                      )}

                      {row.particular.sub5 && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="primary"
                          sx={{
                            cursor: row.bookingId ? 'pointer' : 'default',
                            '&:hover': {
                              textDecoration: row.bookingId ? 'underline' : 'none',
                            },
                          }}
                          onClick={() => {
                            if (row.bookingId) {
                              history.push(`/app/booking/BookingDetails/${row.bookingId}`);
                            }
                          }}
                        >
                          {row.particular.sub5}
                        </Typography>
                      )}
                      {row.particular.sub1 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.particular.sub1}
                        </Typography>
                      )}
                      {row.particular.sub2 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.particular.sub2}
                        </Typography>
                      )}

                      {row.particular.sub4 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.particular.sub4}
                        </Typography>
                      )}
                      {row.particular.sub3 && (
                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                          {row.particular.sub3}
                        </Typography>
                      )}
                      {row.particular.sub8 && (
                        <Typography variant="caption" display="block" color="text.secondary" >
                          {row.particular.sub8}
                        </Typography>
                      )}
                      {row.particular.sub7 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.particular.sub7}
                        </Typography>
                      )}
                      {row.particular.sub9 && (
                        <Typography variant="caption" display="block" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {row.particular.sub9}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatAmount(row.drAmount)}
                  </TableCell>

                  <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatAmount(row.crAmount)}
                  </TableCell>

                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' }}>
                    {formatAmount(row.balance)}
                  </TableCell>

                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {row.description}
                  </TableCell>
                </TableRow>
              ))}

              {/* Grand Total Row */}
              {!loading && (
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell colSpan={3} sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'right' }}>
                    Grand Total
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatAmount(totalDebit)}
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatAmount(totalCredit)}
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatAmount(closingBalance)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination

          component="div"
          count={pagination.totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

    </Box>
  );
};

export default AssociateLedger;